<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useQuasar } from 'quasar';
import CreateConsultationDialog from '@/components/dialogs/CreateConsultationDialog.vue';
import ReorderableTable from '@/components/ReorderableTable.vue';
import RoleVisibilityGuard from '@/components/RoleVisibilityGuard.vue';
import ConsultationTemplateList from '@/components/ConsultationTemplateList.vue';
import CreateConsultationTemplateDialog from '@/components/dialogs/CreateConsultationTemplateDialog.vue';
import { intlFormat } from 'date-fns';
import type { ConsultationDto } from '@/services/types/api.types';
import { useAuthStore, useCommunityStore, useConsultationsStore, useConsultationTemplatesStore } from '@/stores';
import { enCA, frCA } from 'date-fns/locale';
import { currentLocale } from '@/plugins/i18n';
import { Constants } from '@/constants';
import { pageAfterDeletion } from '@/composables/pagination-utils';
import { getTranslation } from '@/composables/data-display-utils';
import { confirmDelete } from '@/helpers/dialogs.helper';

const router = useRouter();
const { t } = useI18n();
const $q = useQuasar();

// STORE
const authStore = useAuthStore();
const consultationsStore = useConsultationsStore();
const consultationTemplateStore = useConsultationTemplatesStore();
consultationsStore.fetchConsultations();
const communityStore = useCommunityStore();
const { community } = storeToRefs(communityStore);
const { isLoading, pagination, consultationListItems } = storeToRefs(consultationsStore);

// COMPUTED
const columns = computed(() => [
  {
    name: 'title',
    label: t('global.label.title'),
    field: (row: ConsultationDto) => getTranslation(row, 'title', row.language_enabled),
    align: 'left',
  },
  {
    name: 'status',
    label: t('global.label.status'),
    field: (row: ConsultationDto) => getStatus(row),
    align: 'left',
  },
  {
    name: 'project',
    label: t('global.label.number-of-related-projects'),
    field: (row: ConsultationDto) => row.projects.length,
    align: 'center',
  },
  {
    name: 'begin_date',
    label: t('global.label.beginDate'),
    field: (row: ConsultationDto) => row.begin_date && formatDate(new Date(row.begin_date)),
    align: 'left',
  },
  {
    name: 'end_date',
    label: t('global.label.endDate'),
    field: (row: ConsultationDto) => row.end_date && formatDate(new Date(row.end_date)),
    align: 'left',
  },
]);

const actionMenuItems = computed(() => {
  if (isAdmin()) {
    return [
      { action: 'create-template', label: t('consultation.create_template') },
      { action: 'delete-row', label: t('global.label.delete') },
    ];
  }
  return [];
});

const fetchConsultations = async (request: any) => {
  await consultationsStore.fetchConsultations(request.page, request.rowsPerPage, request.q);
};

function getStatus(consultation: ConsultationDto) {
  const status = consultationsStore.getConsultationStatuses();
  return status.find((s) => s.value === consultation.status).label;
}

function formatDate(date: Date) {
  const locale = currentLocale() === 'fr' ? frCA : enCA;
  return intlFormat(date, { locale });
}

function isAdmin() {
  return authStore.role === Constants.User.Role.ADMIN;
}

function getDefaultLanguageEnabled() {
  return [community.value?.default_language ?? Constants.DefaultLocale];
}

const handleRowClick = (row: ConsultationDto) => {
  router.push({ name: 'consultation_details', params: { id: row.id } });
};

const handleAddRow = () => {
  const consultation: ConsultationDto = consultationsStore.createConsultation({
    language_enabled: getDefaultLanguageEnabled(),
  });
  $q.dialog({
    component: CreateConsultationDialog,
    componentProps: {
      consultation,
      languageEnabled: getDefaultLanguageEnabled(),
    },
  });
};

const handleDeleteRow = (row: ConsultationDto, q: string | null = null) => {
  if (!row.id) {
    console.warn('Row does not have an id');
    return;
  }

  confirmDelete(t('consultation.name')).onOk(() => {
    const page = pageAfterDeletion(pagination.value);
    consultationsStore.delete(row.id!, page, q);
  });
};

const handleActions = async (action: any) => {
  switch (action.action) {
    case 'create-template':
      const consultationId = action.payload.row.id;
      await consultationsStore.fetchConsultation(consultationId);
      const consultation = consultationsStore.getItem(consultationId);
      $q.dialog({
        component: CreateConsultationTemplateDialog,
        componentProps: {
          consultationTemplate: consultationTemplateStore.create({
            title: consultation?.title,
            description: consultation?.description,
            cloneFromId: consultation?.id,
            language_enabled: consultation?.language_enabled ?? getDefaultLanguageEnabled(),
          }),
        },
      });
      break;
  }
};
</script>

<template>
  <q-page class="consultations-list">
    <h1 class="title">{{ t('consultation.consultations') }}</h1>
    <reorderable-table
      :rows="consultationListItems"
      :columns="columns"
      :loading="isLoading"
      itemKey="id"
      :disable-add-button="!isAdmin()"
      :disable-delete-button="true"
      :disableOrderable="true"
      :pagination-enabled="true"
      :pagination="pagination"
      :search-enabled="true"
      :action-menu-items="actionMenuItems"
      @row-click="handleRowClick"
      @add-row="handleAddRow"
      @delete-row="handleDeleteRow"
      @request="fetchConsultations"
      @actions="handleActions"
    />
    <role-visibility-guard :rolesAllowed="[Constants.User.Role.ADMIN]">
      <q-separator spaced="xl" />
      <consultation-template-list />
    </role-visibility-guard>
  </q-page>
</template>
<style lang="scss">
.consultations-list {
  padding: 16px;

  .title {
    margin: 0 10px !important;
  }
}
</style>
